import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { locationsAPI, type EventType } from '../services/api';
import { newsService, type NewsItem } from '../services/newsService';
import { blogService, type BlogArticle } from '../services/blogService';
import {
  Box, Typography, Button, Grid, Paper, Container, Chip, Checkbox, Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  People,
  CalendarToday,
  TrendingUp,
  Campaign,
  QrCode,
  Share,
  Analytics,
  EmojiEvents,
  GroupAdd,
  Speed,
} from '@mui/icons-material';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, setLoginModalOpen } = useAuthStore();
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [latestNews, setLatestNews] = useState<NewsItem[]>([]);
  const [latestBlog, setLatestBlog] = useState<BlogArticle[]>([]);
  const [typesExpanded, setTypesExpanded] = useState(false);
  const [selectedTypeIds, setSelectedTypeIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    locationsAPI.getEventTypes().then(setEventTypes).catch(() => {});
    newsService.getLatest(2).then(setLatestNews).catch(() => {});
    blogService.listArticles({ limit: 3 }).then(d => setLatestBlog(d.articles)).catch(() => {});
  }, []);

  const handleToggleType = (typeId: number) => {
    setSelectedTypeIds(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  };

  const handleShowSelected = () => {
    if (selectedTypeIds.size === 1) {
      navigate(`/events?type_id=${[...selectedTypeIds][0]}`);
    } else if (selectedTypeIds.size > 1) {
      navigate(`/events?type_ids=${[...selectedTypeIds].join(',')}`);
    } else {
      navigate('/events');
    }
  };

  const VISIBLE_COUNT = 5;

  const handleCreate = () => {
    if (!isAuthenticated) { setLoginModalOpen(true); return; }
    navigate('/events/create');
  };

  const features = [
    { icon: <SearchIcon sx={{ fontSize: 28 }} />, title: 'Удобный поиск', desc: 'Фильтруйте по виду спорта, городу, метро, дате и цене' },
    { icon: <GroupAdd sx={{ fontSize: 28 }} />, title: 'Быстрая запись', desc: 'Запишитесь на мероприятие в пару кликов' },
    { icon: <People sx={{ fontSize: 28 }} />, title: 'Найдите компанию', desc: 'Знакомьтесь с единомышленниками и находите друзей' },
    { icon: <CalendarToday sx={{ fontSize: 28 }} />, title: 'Свои мероприятия', desc: 'Создавайте и управляйте своими событиями' },
  ];

  const organizerFeatures = [
    { icon: <Speed sx={{ fontSize: 28 }} />, title: 'Простое создание', desc: 'Создайте мероприятие за 2 минуты' },
    { icon: <People sx={{ fontSize: 28 }} />, title: 'Управление участниками', desc: 'Одобрение заявок, напоминания, чат с участниками' },
    { icon: <Analytics sx={{ fontSize: 28 }} />, title: 'Аналитика', desc: 'Статистика просмотров, записей и конверсия' },
    { icon: <TrendingUp sx={{ fontSize: 28 }} />, title: 'Рост аудитории', desc: 'Ваше мероприятие увидят тысячи пользователей' },
  ];

  const promoTools = [
    { icon: <Share sx={{ fontSize: 24 }} />, title: 'Поделиться ссылкой', desc: 'Пригласительные ссылки для быстрого распространения' },
    { icon: <QrCode sx={{ fontSize: 24 }} />, title: 'QR-коды', desc: 'Генерация QR-кодов для офлайн-продвижения' },
    { icon: <Campaign sx={{ fontSize: 24 }} />, title: 'Рассылки', desc: 'Напоминания участникам о предстоящих событиях' },
    { icon: <EmojiEvents sx={{ fontSize: 24 }} />, title: 'Рейтинг организатора', desc: 'Репутация и отзывы привлекают больше участников' },
  ];

  return (
    <Box>
      {/* Hero banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0a3d0a 0%, #145214 50%, #1b5e20 100%)',
          color: '#ffffff',
          pt: { xs: 1.5, sm: 2.5 },
          pb: { xs: 1.5, sm: 2.5 },
          px: { xs: 2, sm: 4 },
          textAlign: 'center',
          borderRadius: 3,
          mx: { xs: 1, sm: 2 },
          mt: { xs: 1, sm: 2 },
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant="h1"
            fontWeight={900}
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
              lineHeight: 1.15,
              mb: 1.5,
              letterSpacing: '-0.03em',
            }}
          >
            Nashrelax
            <Box component="span" sx={{ color: '#ffa726' }}>.ru</Box>
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
              fontWeight: 500,
              color: 'rgba(255,255,255,0.9)',
              mb: 0.75,
              lineHeight: 1.4,
            }}
          >
            Организация спортивных мероприятий, активного досуга и развлечений
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.75)',
              mb: 2.5,
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
            }}
          >
            Найдите друзей и сделайте свой досуг ярким!
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 1.5 }, justifyContent: 'center', mb: 2, alignItems: 'stretch' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{
                bgcolor: '#ef6c00',
                '&:hover': { bgcolor: '#e65100' },
                py: { xs: 0.75, sm: 1.5 },
                px: { xs: 2, sm: 3.5 },
                fontSize: { xs: '0.78rem', sm: '0.95rem' },
                fontWeight: 700,
                borderRadius: 2,
                flex: 1,
                minWidth: 0,
              }}
            >
              Создать мероприятие
            </Button>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/events')}
              sx={{
                bgcolor: '#81c784',
                '&:hover': { bgcolor: '#66bb6a' },
                color: '#1e293b',
                py: { xs: 0.75, sm: 1.5 },
                px: { xs: 2, sm: 3.5 },
                fontSize: { xs: '0.78rem', sm: '0.95rem' },
                fontWeight: 700,
                borderRadius: 2,
                flex: 1,
                minWidth: 0,
              }}
            >
              Найти мероприятие
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Event types catalog */}
      {eventTypes.length > 0 && (
        <Box sx={{ bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', py: { xs: 1.5, sm: 2 } }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                Виды мероприятий
              </Typography>
              {selectedTypeIds.size > 0 && (
                <Button size="small" variant="contained" onClick={handleShowSelected}
                  sx={{ fontSize: '0.72rem', py: 0.25, px: 1.5, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                  Показать ({selectedTypeIds.size})
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {eventTypes.slice(0, VISIBLE_COUNT).map((type) => (
                <Chip
                  key={type.id}
                  label={`${type.icon_emoji || '🎯'} ${type.name}`}
                  onClick={() => handleToggleType(type.id)}
                  onDelete={selectedTypeIds.has(type.id) ? () => handleToggleType(type.id) : undefined}
                  deleteIcon={selectedTypeIds.has(type.id) ? <Checkbox checked size="small" sx={{ p: 0, color: '#fff !important', '& .MuiSvgIcon-root': { fontSize: 16 } }} /> : undefined}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    ...(selectedTypeIds.has(type.id) && { bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }),
                  }}
                  variant={selectedTypeIds.has(type.id) ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
              {eventTypes.length > VISIBLE_COUNT && (
                <Chip
                  label={typesExpanded ? 'Свернуть' : `Прочее (${eventTypes.length - VISIBLE_COUNT})`}
                  onClick={() => setTypesExpanded(!typesExpanded)}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
                />
              )}
            </Box>
            <Collapse in={typesExpanded}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {eventTypes.slice(VISIBLE_COUNT).map((type) => (
                  <Chip
                    key={type.id}
                    label={`${type.icon_emoji || '🎯'} ${type.name}`}
                    onClick={() => handleToggleType(type.id)}
                    onDelete={selectedTypeIds.has(type.id) ? () => handleToggleType(type.id) : undefined}
                    deleteIcon={selectedTypeIds.has(type.id) ? <Checkbox checked size="small" sx={{ p: 0, color: '#fff !important', '& .MuiSvgIcon-root': { fontSize: 16 } }} /> : undefined}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      ...(selectedTypeIds.has(type.id) && { bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }),
                    }}
                    variant={selectedTypeIds.has(type.id) ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </Collapse>
          </Container>
        </Box>
      )}

      {/* Features for participants */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h6"
          fontWeight={800}
          textAlign="center"
          gutterBottom
          sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.2rem' } }}
        >
          Для участников
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {features.map((f, i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <Paper
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: '#ffffff',
                }}
              >
                <Box sx={{ color: '#1565c0', mb: 0.25 }}>{f.icon}</Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
                  {f.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  {f.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features for organizers */}
      <Box sx={{ bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', py: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h6"
            fontWeight={800}
            textAlign="center"
            gutterBottom
            sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.2rem' } }}
          >
            Для организаторов
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {organizerFeatures.map((f, i) => (
              <Grid item xs={6} sm={6} md={3} key={i}>
                <Paper
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    border: '1px solid #e2e8f0',
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Box sx={{ color: '#ef6c00', mb: 0.25 }}>{f.icon}</Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    {f.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: { xs: 2, sm: 2.5 } }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{
                bgcolor: '#ef6c00',
                '&:hover': { bgcolor: '#e65100' },
                py: 1.25,
                px: 3.5,
                fontWeight: 700,
              }}
            >
              Создать мероприятие
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Promo tools */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h6"
          fontWeight={800}
          textAlign="center"
          gutterBottom
          sx={{ mb: 0.5, fontSize: { xs: '1rem', sm: '1.2rem' } }}
        >
          Инструменты продвижения
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: { xs: 1.5, sm: 2 }, maxWidth: 440, mx: 'auto', fontSize: '0.85rem' }}
        >
          Привлекайте участников с помощью встроенных инструментов
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {promoTools.map((t, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                  gap: 1,
                  p: { xs: 1.5, sm: 2 },
                  textAlign: { xs: 'center', md: 'left' },
                  bgcolor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box sx={{ color: '#2e7d32', flexShrink: 0 }}>{t.icon}</Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                    {t.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.72rem', mt: 0.25 }}>
                    {t.desc}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Blog section */}
      {latestBlog.length > 0 && (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
              Блог
            </Typography>
            <Button component={RouterLink} to="/blog" size="small" sx={{ fontSize: '0.78rem', color: '#1565c0' }}>
              Все статьи
            </Button>
          </Box>
          <Grid container spacing={1.5}>
            {latestBlog.map(article => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Paper component={RouterLink} to={`/blog/${article.id}`}
                  sx={{ p: { xs: 1.5, sm: 2 }, textDecoration: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', flexDirection: 'column', height: '100%', '&:hover': { borderColor: '#1565c0' } }}>
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75 }}>
                    {article.event_type_name && <Chip size="small" label={`${article.event_type_emoji || ''} ${article.event_type_name}`} sx={{ height: 20, fontSize: '0.65rem' }} />}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', mb: 0.25, lineHeight: 1.3 }}>{article.title}</Typography>
                  {article.excerpt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
                      {article.excerpt}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                    {article.author_name} · {article.published_at ? new Date(article.published_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : ''}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* News section */}
      {latestNews.length > 0 && (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
              Новости Nashrelax.ru
            </Typography>
            <Button component={RouterLink} to="/news" size="small" sx={{ fontSize: '0.78rem', color: '#1565c0' }}>
              Все новости
            </Button>
          </Box>
          <Grid container spacing={1.5}>
            {latestNews.map(item => (
              <Grid item xs={12} sm={6} key={item.id}>
                <Paper
                  component={RouterLink}
                  to={`/news/${item.id}`}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    gap: 1.5,
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#1565c0' },
                  }}
                >
                  <Box sx={{
                    width: 40, height: 40, borderRadius: 2,
                    bgcolor: item.cover_color || '#1565c0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 800, fontSize: '1rem', flexShrink: 0,
                  }}>
                    {item.id}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', mb: 0.25 }}>
                      {item.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 12, color: '#999' }} />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.published_at || item.created_at || '').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* CTA bottom */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0a3d0a 0%, #145214 100%)',
          color: '#ffffff',
          py: { xs: 2.5, sm: 3.5 },
          px: { xs: 2, sm: 4 },
          textAlign: 'center',
          borderRadius: 3,
          mx: { xs: 1, sm: 2 },
          mb: { xs: 1, sm: 2 },
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              mb: 2,
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              lineHeight: 1.5,
              fontWeight: 500,
            }}
          >
            Nashrelax.ru — здесь люди ищут людей, друзей и единомышленников, с которыми можно приятно провести время.
          </Typography>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
            Готовы начать?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
            Найдите мероприятие по душе или создайте своё
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'center', alignItems: 'stretch' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleCreate}
              sx={{
                bgcolor: '#ef6c00',
                '&:hover': { bgcolor: '#e65100' },
                py: 1.25,
                px: 3.5,
                fontWeight: 700,
                flex: 1,
                minWidth: 0,
              }}
            >
              Создать мероприятие
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/events')}
              sx={{
                bgcolor: '#81c784',
                '&:hover': { bgcolor: '#66bb6a' },
                color: '#1e293b',
                py: 1.25,
                px: 3.5,
                fontWeight: 700,
                flex: 1,
                minWidth: 0,
              }}
            >
              Смотреть мероприятия
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;